/**
 * header-drawer.js
 * Two-level mobile navigation drawer.
 *
 * Native <dialog> + showModal(), so the focus trap, the top layer and the
 * backdrop come from the browser; `scroll-lock` on the element engages the
 * global rule in critical.css. Levels are swapped with `hidden`, which keeps
 * the inactive panel out of both the tab order and the accessibility tree —
 * the reason for panel-replacement over a translated track, which would leave
 * the off-screen panel tabbable inside the trap.
 *
 * Focus: pushing moves to the level-2 heading (tabindex="-1") so the change is
 * announced; popping restores the level-1 button that opened it.
 *
 * Escape is intercepted while a sub-panel is showing so it steps back one
 * level instead of the browser closing the whole dialog. At root level the
 * `cancel` event is intercepted instead, so Escape animates out like every
 * other close path rather than snapping shut.
 *
 * Also fixes a pre-existing bug: the drawer is display:none at >=990px, but
 * `display` does not remove an open modal dialog from the top layer, so the
 * global scroll-lock kept <html> locked with nothing visible after a resize.
 *
 * Contract: [data-header-drawer] dialog, [data-drawer-toggle] (delegated from
 * document, matched via aria-controls), [data-drawer-close],
 * [data-drawer-back], [data-drawer-open="i"], [data-drawer-panel="root"|"i"],
 * [data-closing] written on the dialog for the duration of the exit animation.
 */
(function () {
  'use strict';

  var DESKTOP = '(min-width: 990px)';
  var CLOSE_MS = 100; // must match @keyframes header-drawer-out in header.liquid

  // Deferred close, so the drawer can animate out. [open] is gone the instant
  // the dialog leaves the top layer, which leaves an exit animation nothing to
  // run from — so mark the element, let the animation play, then close for
  // real. Every user-initiated close path goes through here; the breakpoint
  // handler still closes outright, since animating a drawer that has just
  // become display:none would only delay the unlock.
  function requestClose(dialog) {
    if (!dialog.open || dialog.hasAttribute('data-closing')) return;

    var timer;
    var finish = function () {
      clearTimeout(timer);
      dialog.removeEventListener('animationend', onEnd);
      dialog.removeAttribute('data-closing');
      if (dialog.open) dialog.close();
    };
    var onEnd = function (event) {
      // animationend bubbles — ignore anything a descendant is animating.
      if (event.target === dialog) finish();
    };

    // Under prefers-reduced-motion the animation is `none`, so animationend
    // never fires and this timeout is the actual close path, not a fallback.
    timer = setTimeout(finish, CLOSE_MS + 50);
    dialog.setAttribute('data-closing', '');
    dialog.addEventListener('animationend', onEnd);
  }

  function init(dialog) {
    if (dialog.__drawerAbort) dialog.__drawerAbort.abort();
    var ac = (dialog.__drawerAbort = new AbortController());
    var signal = ac.signal;

    var root = dialog.querySelector('[data-drawer-panel="root"]');
    var activePanel = null;
    var returnFocus = null;

    function pop() {
      if (!activePanel) return;
      activePanel.hidden = true;
      var opener = returnFocus;
      activePanel = null;
      returnFocus = null;
      if (root) root.hidden = false;
      dialog
        .querySelectorAll('[data-drawer-open]')
        .forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      if (opener && opener.isConnected) opener.focus();
    }

    function push(id, opener) {
      var next = dialog.querySelector('[data-drawer-panel="' + id + '"]');
      if (!next) return;
      if (activePanel) activePanel.hidden = true;
      if (root) root.hidden = true;
      next.hidden = false;
      activePanel = next;
      returnFocus = opener || null;
      if (opener) opener.setAttribute('aria-expanded', 'true');
      // Start each level at the top rather than inheriting level 1's scroll.
      next.scrollTop = 0;
      var title = next.querySelector('[tabindex="-1"]');
      if (title) title.focus();
    }

    function reset() {
      if (activePanel) activePanel.hidden = true;
      activePanel = null;
      returnFocus = null;
      if (root) root.hidden = false;
      dialog
        .querySelectorAll('[data-drawer-open]')
        .forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    }

    dialog.addEventListener(
      'click',
      function (event) {
        var opener = event.target.closest('[data-drawer-open]');
        if (opener) {
          push(opener.getAttribute('data-drawer-open'), opener);
          return;
        }
        if (event.target.closest('[data-drawer-back]')) {
          pop();
          return;
        }
        if (event.target.closest('[data-drawer-close]')) {
          requestClose(dialog);
          return;
        }
        // Backdrop click — target is the dialog itself only when the pointer
        // landed outside the panel box.
        if (event.target === dialog) requestClose(dialog);
      },
      { signal: signal }
    );

    dialog.addEventListener(
      'keydown',
      function (event) {
        if (event.key === 'Escape' && activePanel) {
          // Step back a level rather than letting <dialog> close outright.
          event.preventDefault();
          pop();
        }
      },
      { signal: signal }
    );

    dialog.addEventListener(
      'cancel',
      function (event) {
        event.preventDefault();
        requestClose(dialog);
      },
      { signal: signal }
    );

    dialog.addEventListener(
      'close',
      function () {
        dialog.removeAttribute('data-closing');
        reset();
        document
          .querySelectorAll('[data-drawer-toggle][aria-controls="' + dialog.id + '"]')
          .forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
      },
      { signal: signal }
    );

    var mq = window.matchMedia(DESKTOP);
    var onChange = function (event) {
      if (event.matches && dialog.open) dialog.close();
    };
    if (mq.addEventListener) mq.addEventListener('change', onChange, { signal: signal });

    reset();
  }

  // Delegated from document so it survives the header being re-rendered.
  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-drawer-toggle]');
    if (!toggle) return;
    var id = toggle.getAttribute('aria-controls');
    var dialog = id && document.getElementById(id);
    if (!dialog || typeof dialog.showModal !== 'function' || dialog.open) return;
    event.preventDefault();
    dialog.showModal();
    toggle.setAttribute('aria-expanded', 'true');
  });

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-header-drawer]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
})();
